import { useEffect } from 'react';
import * as THREE from 'three';
import { applyMaterialProperties, getMaterialProperties } from '../utils/materials';
import { isMaterialDebugEnabled, materialDebugLog } from '../utils/materialDebug';

/**
 * Applies one-time post-load processing to a GLTF scene.
 *
 * Responsibilities:
 * - Shadow casting/receiving on all meshes
 * - Hiding embedded lights (viewer uses its own lighting rig)
 * - Material property overrides via getMaterialProperties
 * - frustumCulled = false to prevent pop-in at extreme angles
 * - Bounding box computation and reporting via onBoundsComputed
 */
export function useModelProcessing(
  scene: THREE.Object3D | null | undefined,
  onBoundsComputed?: (bounds: THREE.Box3, center: THREE.Vector3) => void,
  animationNames?: string[],
): void {
  // Shadows and light visibility
  useEffect(() => {
    if (!scene) return;
    scene.traverse((child: any) => {
      if (child.isLight) {
        child.visible = false;
        return;
      }
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }, [scene]);

  // Material overrides
  useEffect(() => {
    if (!scene) return;

    const applyToMaterial = (material: THREE.Material) => {
      if (!(material instanceof THREE.MeshStandardMaterial)) return;
      if (material.type !== 'MeshStandardMaterial') return;
      applyMaterialProperties(material, getMaterialProperties(material.color));
    };

    scene.traverse((child: any) => {
      if (!child.isMesh) return;

      child.frustumCulled = false;

      if (Array.isArray(child.material)) {
        child.material.forEach(applyToMaterial);
      } else if (child.material) {
        applyToMaterial(child.material);
      }
    });

    // ── Opt-in diagnostics (?debugMaterials=1) ──────────────────────────
    // Summarizes the post-override material state to determine whether the
    // metallic override actually ran and on what material types.
    if (isMaterialDebugEnabled()) {
      let meshCount = 0;
      let standardCount = 0;
      const typeCounts: Record<string, number> = {};
      const samples: Array<Record<string, unknown>> = [];

      scene.traverse((child: any) => {
        if (!child.isMesh) return;
        meshCount += 1;
        const mats: THREE.Material[] = Array.isArray(child.material)
          ? child.material
          : child.material
            ? [child.material]
            : [];
        for (const mat of mats) {
          typeCounts[mat.type] = (typeCounts[mat.type] ?? 0) + 1;
          if (mat instanceof THREE.MeshStandardMaterial) {
            standardCount += 1;
            if (samples.length < 5) {
              samples.push({
                mesh: child.name || '(unnamed)',
                type: mat.type,
                metalness: mat.metalness,
                roughness: mat.roughness,
                envMapIntensity: mat.envMapIntensity,
                hasMetalnessMap: Boolean(mat.metalnessMap),
                hasEnvMap: Boolean(mat.envMap),
                color: `#${mat.color.getHexString()}`,
              });
            }
          }
        }
      });

      materialDebugLog('useModelProcessing material summary', {
        meshCount,
        standardMaterialCount: standardCount,
        materialTypeCounts: typeCounts,
        sampledStandardMaterials: samples,
      });
    }
  }, [scene]);

  // Bounding box computation and diagnostics
  useEffect(() => {
    if (!scene) return;
    const box = new THREE.Box3();

    scene.traverse((child: any) => {
      if (child.isMesh) {
        box.expandByObject(child);
      }
    });

    const center = box.getCenter(new THREE.Vector3());

    onBoundsComputed?.(box, center);
  }, [scene, onBoundsComputed, animationNames]);
}
